import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, stagger, viewport } from "@/lib/motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowLeft, Calendar, Mail } from "lucide-react";
import OrbitalDiagram from "@/components/OrbitalDiagram";

const EXPANSIVE_BG =
  "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=2400&q=80";

const PILLARS = [
  { word: "Connect", text: "across disciplines, geographies and lived experience." },
  { word: "Experiment", text: "with new rituals, formats and relational practices." },
  { word: "Prototype", text: "small living models of care, learning and belonging." },
  { word: "Co-create", text: "systems rooted in relationship and collective well-being." },
];

export default function HarmoniaPage() {
  return (
    <div className="bg-forest min-h-screen text-bone">
      <Nav />

      {/* HERO — same deep-forest expansive feel as the on-landing section */}
      <header
        className="relative pt-32 md:pt-44 pb-24 md:pb-32 overflow-hidden bg-forest"
        data-testid="harmonia-page"
      >
        <div className="absolute inset-0 -z-0 bg-forest">
          <img src={EXPANSIVE_BG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-forest/65" />
          <div className="absolute inset-0 bg-gradient-to-b from-forestDeep/50 via-transparent to-forestDeep/70" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link
            to="/"
            data-testid="harmonia-back"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-clay/80 hover:text-bronze transition-colors duration-500"
          >
            <ArrowLeft size={14} /> Back home
          </Link>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-14 text-[10px] uppercase tracking-[0.3em] text-bronze"
          >
            ✦ &nbsp; Harmonia &nbsp; ✦
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-6 font-serif text-7xl md:text-9xl text-bone leading-[0.9] tracking-tight italic"
          >
            Harmonia
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-6 font-serif italic text-2xl md:text-3xl text-bronze max-w-2xl mx-auto leading-snug"
          >
            A relational ecosystem exploring new models of care, collaboration,
            and community design.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-forestDeep border border-clay/30 text-clay/90 text-[10px] uppercase tracking-[0.3em]"
          >
            <span className="w-2 h-2 rounded-full bg-bronze animate-pulse" />
            Coming soon
          </motion.div>
        </div>
      </header>

      {/* BODY */}
      <section className="relative bg-sand text-charcoal py-24 md:py-32 overflow-hidden">
        <div className="absolute top-20 -left-12 w-72 opacity-15 pointer-events-none">
          <OrbitalDiagram />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.15)}
          >
            <motion.p
              variants={fadeUp}
              className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6"
            >
              An interdisciplinary ecosystem
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="font-serif text-3xl md:text-4xl text-charcoal leading-snug"
            >
              Harmonia is an{" "}
              <span className="italic text-bronze">interdisciplinary ecosystem</span>{" "}
              exploring new models of collective care, collaboration, and
              community design.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-8 text-lg text-ink leading-relaxed font-light"
            >
              It brings together people across disciplines to:
            </motion.p>

            <motion.ul
              variants={stagger(0.08)}
              className="mt-8 grid sm:grid-cols-2 gap-6"
            >
              {PILLARS.map((p) => (
                <motion.li
                  key={p.word}
                  variants={fadeUp}
                  data-testid={`harmonia-pillar-${p.word.toLowerCase()}`}
                  className="border-l border-bronze/40 pl-5"
                >
                  <p className="font-serif italic text-2xl text-bronze">
                    {p.word.toLowerCase()}
                  </p>
                  <p className="mt-2 text-sm text-ink/85 leading-relaxed font-light">
                    {p.text}
                  </p>
                </motion.li>
              ))}
            </motion.ul>

            <motion.p
              variants={fadeUp}
              className="mt-12 text-lg md:text-xl text-ink leading-relaxed font-light text-pretty"
            >
              Together, we co-create new systems rooted in{" "}
              <span className="italic font-serif text-bronze text-[1.3em]">
                belonging
              </span>
              , relationship, and collective well-being.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CTA — invitation + open question */}
      <section className="relative bg-forest text-bone py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 -z-0 bg-forest">
          <img src={EXPANSIVE_BG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-forest/70" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6">
            ✦ &nbsp; Stay close &nbsp; ✦
          </p>
          <h3 className="font-serif text-4xl md:text-5xl text-bone leading-tight">
            The portal is opening, <span className="italic text-bronze">slowly.</span>
          </h3>
          <p className="mt-6 text-bone/85 leading-relaxed font-light max-w-2xl mx-auto">
            If you&apos;d like to be part of what Harmonia is becoming, write a
            few words. We are gathering people who want to think, build and
            care together.
          </p>

          {/* Reflective question */}
          <div className="mt-12 pt-10 border-t border-bone/15 max-w-2xl mx-auto">
            <p className="text-[10px] uppercase tracking-[0.3em] text-clay/70 mb-5">
              ✦ &nbsp; For now, let&apos;s keep reflecting &nbsp; ✦
            </p>
            <p className="font-serif italic text-2xl md:text-[1.85rem] text-bronze leading-snug">
              How do we create real ecosystems of mutual care, collaboration,
              and regeneration across disciplines?
            </p>
          </div>

          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link
              to="/#connect"
              data-testid="harmonia-connect-cta"
              className="btn-pill inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-bone text-forest text-[11px] uppercase tracking-[0.22em] hover:bg-bronze hover:text-bone"
            >
              <Mail size={14} /> Send a note
            </Link>
            <a
              href="https://calendly.com/harmoniaportal-5"
              target="_blank"
              rel="noreferrer"
              data-testid="harmonia-calendly-cta"
              className="btn-pill inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-bone/40 text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-bone hover:text-forest"
            >
              <Calendar size={14} /> Or schedule a call
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
